/**
 * @format
 */
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { Store } from "@ngrx/store";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { SelectItem } from "primeng/api";
import { Observable } from "rxjs/internal/Observable";
import { combineLatest } from "rxjs/internal/observable/combineLatest";
import { Subject } from "rxjs/internal/Subject";
import { Subscription } from "rxjs/internal/Subscription";
import { filter, map, tap } from "rxjs/operators";
import { AdaptiveRulesComponent } from "../adaptive-rules/adaptive-rules.component";
import { AuthenticationSelectors } from "../auth/state/authentication.selectors";
import { ContenteditableDirective } from "../core/content-editable/contenteditable.directive";
import { BsModalConfirmComponent, createConfirmModalOptions } from "../core/modal/modal-confirm.component";
import { Provider } from "../core/model/central/provider.model";
import { Reason } from "../core/model/central/reason.model";
import { User } from "../core/model/user.model";
import { ReasonsSettingsActions } from "../reasons/state/reasons-settings.actions";
import { ReasonsSettingsSelectors } from "../reasons/state/reasons-settings.selectors";
import { AppointmentListService } from "../reminders/appointment-list/state/appointment-list.service";
import { ProviderService } from "../reminders/reminder-settings/state/provider.service";
import { AppointmentResourceSettingsActions } from "../resources/state/appointment-resource-settings.actions";
import { AppointmentResource } from "../resources/state/appointment-resource-settings.model";
import { AppointmentResourceSettingsSelectors } from "../resources/state/appointment-resource-settings.selectors";
import { PreviewEditTemplateActions } from "./state/preview-edit-template.actions";
import { PreviewEditTemplate } from "./state/preview-edit-template.model";
import { TemplateUpdateModel } from "./state/preview-edit-template.reducer";
import { PreviewEditTemplateSelectors } from "./state/preview-edit-template.selectors";
import { PreviewEditTemplateService } from "./state/preview-edit-template.service";
import { BsModalAlertComponent, createAlertModalOptions } from "../core/modal/modal-alert.component";
import { UserSelectors } from "../user/state/user.selectors";
import { DatePipe } from "@angular/common";
import { SmsConversionService } from "../messages/sms-convertion-ar2-to-ar3/sms-conversion.service";
import { ContentEditableService } from "../core/content-editable/contenteditable.service";

@Component({
   selector: "preview-edit-template-tabs",
   templateUrl: "./preview-edit-template-tabs.component.html",
   styleUrls: ["./preview-edit-template-tabs.component.css"],
   changeDetection: ChangeDetectionStrategy.OnPush,
   encapsulation: ViewEncapsulation.None,
})
export class PreviewEditTemplateTabsComponent implements OnInit, OnDestroy {
   selectedTemplateId: number;
   fileModel: any;
   isOnlyPreview: boolean;
   disablePasteEvent: boolean;
   selectedTemplate: any;
   isMappedToOtherUser: boolean;
   isFileUsedByOtherUser: boolean;
   previewMediaType: string;
   canEditDefault: boolean;
   categoryType: string = undefined;

   templateFileRef: string;
   templateUpdateModel$: Observable<TemplateUpdateModel>;
   updatedSource: any;
   isSourceChanged = false;
   isUpdated = false;
   isModelUpdated = false;
   activeIndex = 0;
   isMediaUpdated = false;
   mustUpdateModel = false;

   templateParameters$: Observable<Array<SelectItem>>;
   loading$: Observable<boolean>;
   selectedUser$: Observable<User>;
   locations$: Observable<any>;
   locationEntities$: Observable<any>;
   providers$: Observable<Provider>;
   reasons$: Observable<Array<Reason>>;
   appointmentResources$: Observable<Array<AppointmentResource>>;
   loadingMessageContentByTemplateId$: Observable<boolean>;
   isModelProcessed$: Observable<boolean>;
   viewModel$: Observable<any>;
   mediaDevices$: Observable<any>;
   currentUser$: Observable<User>;
   user$: Observable<any>;
   isMediaProcessed$: Observable<boolean>;
   isAdmin$: Observable<boolean>;
   selectedUserAccountSetting$: Observable<any>;
   templateState$: Observable<any>;
   isSaving$: Observable<boolean>;

   onClose: Subject<boolean>;
   previewEditTemplate$: Observable<PreviewEditTemplate>;
   errorMessage$: Observable<string>;
   emailSource$: Observable<any>;
   totalStrategiesMapped$: Observable<any>;

   templateFilename: string;
   isFromUploadDialog = false;
   templateCloned = false;
   savedCustomTemplate$: Observable<boolean>;
   templateLastModified$: Observable<any>;
   lastModified: any;
   updatedTemplateFileRef: string;
   hasValueFromDb = false;
   subscription = new Subscription();
   requireVoiceFirstNameTTS = false;
   totalStrategies = -1;
   hasAutoremind3Permission = false;
   canUpdateTemplateInUsePermission = false;
   canAccessAdaptive = false;
   pipe = new DatePipe("en-US");
   displayConversionSMS$: Observable<boolean>;

   constructor(
      private authenticationSelectors: AuthenticationSelectors,
      private previewEditTemplateService: PreviewEditTemplateService,
      private previewEditSelector: PreviewEditTemplateSelectors,
      private previewBsModalRef: BsModalRef,
      private contenteditableDirective: ContenteditableDirective,
      private userSelectors: UserSelectors,
      private providerService: ProviderService,
      private appointmentListService: AppointmentListService,
      private reasonsSettingsSelectors: ReasonsSettingsSelectors,
      private appointmentResourceSettingsSelectors: AppointmentResourceSettingsSelectors,
      private store: Store<any>,
      private modalService: BsModalService,
      private smsConversionService: SmsConversionService,
      private contenteditableService: ContentEditableService
   ) {
      this.previewEditTemplate$ = this.previewEditSelector.selectGetModel();
      this.isModelProcessed$ = this.previewEditSelector.selectIsModelProcessed();
      this.templateUpdateModel$ = this.previewEditSelector.selectTemplateUpdateModel();
      this.mediaDevices$ = this.previewEditSelector.selectGetMediaDevices();
      this.errorMessage$ = this.previewEditSelector.selectErrorMessage();
      this.currentUser$ = this.authenticationSelectors.selectCurrentUser();
      this.selectedUser$ = this.userSelectors.selectSelectedUser();
      this.isMediaProcessed$ = this.previewEditSelector.selectedIsMediaProcessed();
      this.isAdmin$ = this.authenticationSelectors.selectIsAdmin();

      this.loading$ = combineLatest([
         this.previewEditSelector.selectedLoadingMessageContentByTemplateId(),
         this.previewEditSelector.selectLoading(),
      ]).pipe(map(([loadingMessageContentByTemplateId, loading]) => loadingMessageContentByTemplateId || loading));

      this.savedCustomTemplate$ = this.previewEditSelector.selectGetSavedCustomTemplate();
      this.templateLastModified$ = this.previewEditSelector.selectGetTemplateLastModified();
      this.loadingMessageContentByTemplateId$ = this.previewEditSelector.selectedLoadingMessageContentByTemplateId();

      this.reasons$ = this.reasonsSettingsSelectors.selectReasons().pipe(filter((reasons) => reasons !== undefined));

      this.appointmentResources$ = this.appointmentResourceSettingsSelectors
         .selectAppointmentResources()
         .pipe(filter((resources) => resources !== undefined && resources.length > 0));

      this.locations$ = this.previewEditSelector
         .selectGetLocations()
         .pipe(filter((locations) => locations !== undefined));

      this.locationEntities$ = this.previewEditSelector
         .selectGetLocationEntities()
         .pipe(filter((locations) => locations !== undefined));

      this.providers$ = this.previewEditSelector
         .selectGetProviders()
         .pipe(filter((providers) => providers !== undefined));

      this.selectedUserAccountSetting$ = this.userSelectors.selectSelectedUserAccountSettingSelector();
      this.templateState$ = this.previewEditSelector.selectGetTemplateState();

      this.isSaving$ = this.previewEditSelector.selectIsSaving();

      this.displayConversionSMS$ = this.smsConversionService.listenDisplayButton();
   }

   ngOnInit(): void {
      this.onClose = new Subject();
      const config: any = { ...this.modalService.config.initialState };
      this.fileModel = config.fileModel;
      this.selectedTemplate = config.templateModel !== false ? config.templateModel : undefined;
      this.selectedTemplateId = config.templateModel !== false ? config.templateModel.templateId : undefined;
      this.isOnlyPreview = config.isOnlyPreview;
      this.disablePasteEvent = config.disablePasteEvent !== undefined ? config.disablePasteEvent : true;

      this.templateFileRef =
         config.templateModel !== false ? config.templateModel.templateFileRef : config.fileModel.templateFileRef;
      this.updatedTemplateFileRef = this.templateFileRef;
      this.lastModified = config.templateModel !== false ? config.templateModel.lastModified : undefined;
      this.previewMediaType = this.isOnlyPreview ? this.selectedTemplate.mediaType : undefined;
      this.isMappedToOtherUser = config.templateModel.countOfMappings > 0;
      this.canEditDefault = config.canEditDefault;

      this.isFileUsedByOtherUser = config.templateModel.countOfTemplateFileUsages > 0;
      this.activeIndex = config.activeIndex | 0;
      this.templateParameters$ = this.previewEditSelector.selectGetTemplateParameters();
      this.categoryType = config.templateModel.communicationsCategoryId;
      this.user$ = combineLatest([this.selectedUser$, this.currentUser$, this.selectedUserAccountSetting$]).pipe(
         map(([selectedUser, currentUser, selectedUserAccountSetting]) => ({
            selectedUser,
            currentUser,
            selectedUserAccountSetting,
         }))
      );

      this.user$ = this.user$.pipe(
         filter((user) => user !== undefined),
         tap((user) => {
            this.canAccessAdaptive = false;

            if (user.selectedUser !== undefined && user.selectedUser.userId !== 0) {
               this.store.dispatch(
                  PreviewEditTemplateActions.getTemplateParameters({
                     userId: user.selectedUser.userId,
                     categoryType: this.categoryType,
                  })
               );
               this.store.dispatch(
                  PreviewEditTemplateActions.getCountOfStrategiesMappedWithTemplate({
                     templateId: this.selectedTemplateId.toString(),
                     selectedUserName: user.selectedUser.userName,
                  })
               );
               // TODO: Refactor to not do this when the selected user is still the same the next time the dialog is opened
               // TODO: Consider doing this only once the send message tab is opened
               this.store.dispatch(
                  AppointmentResourceSettingsActions.getAppointmentResourcesAndResources({
                     userid: user.selectedUser.userId,
                  })
               );
               this.store.dispatch(
                  ReasonsSettingsActions.getRemindersReasons({
                     userId: user.selectedUser.userId,
                  })
               );
               this.store.dispatch(
                  PreviewEditTemplateActions.getLocationEntities({
                     userId: user.selectedUser.userId,
                  })
               );
               this.store.dispatch(
                  PreviewEditTemplateActions.getProviders({
                     userId: user.selectedUser.userId,
                  })
               );

               if (user.selectedUser === undefined) {
                  this.hasAutoremind3Permission =
                     user.currentUser.userPermissionKeys.findIndex(
                        (permission: any) => permission === "autoremind3"
                     ) !== -1;
                  user.selectedUser = { userId: 0 };
               } else {
                  this.hasAutoremind3Permission =
                     user.selectedUser.userPermissions.findIndex(
                        (permission: any) => permission.permissionKey === "autoremind3"
                     ) !== -1;
               }

               user.currentUser.userPermissionKeys.map((permission: any) => {
                  if (permission === "admin:template:update:inuse") {
                     this.canUpdateTemplateInUsePermission = true;
                  }
                  return permission;
               });

               if (user.selectedUser === undefined) {
                  user.selectedUser = { userId: 0 };
               }
            } else {
               user.selectedUser = { userId: 0 };
            }

            if (this.hasAutoremind3Permission) {
               this.canAccessAdaptive = true;
            }

            if (this.selectedTemplateId !== undefined) {
               this.store.dispatch(
                  PreviewEditTemplateActions.getModel({
                     templateId: this.selectedTemplateId,
                     templateFileRef: undefined,
                     updatedModel: undefined,
                     selectedUserId: user.selectedUser.userId,
                  })
               );
            } else {
               // This is called from Upload Template (not in use right now)
               this.store.dispatch(
                  PreviewEditTemplateActions.getModel({
                     templateId: 0,
                     templateFileRef: this.updatedTemplateFileRef,
                     updatedModel: this.fileModel.fileModel,
                     selectedUserId: user.selectedUser.userId,
                  })
               );
            }
         })
      );

      this.totalStrategiesMapped$ = this.previewEditSelector.selectTotalStrategiesMapped().pipe(
         map((totalStrategiesMapped) => {
            this.totalStrategies = totalStrategiesMapped;
         })
      );

      this.previewEditTemplate$ = this.previewEditTemplate$.pipe(
         filter((previewEditTemplate) => previewEditTemplate !== undefined),
         filter((previewEditTemplate) => previewEditTemplate.model !== undefined),
         map((previewEditTemplate) => {
            const previewClone: any = { ...previewEditTemplate };
            const templateDefaultContent: any = {};
            const model: any = {};

            this.isModelUpdated = false;
            if (previewClone.templateDefaultContent !== undefined) {
               let templateDefaultContentModelDB: any = JSON.stringify(previewClone.templateDefaultContent);
               this.mustUpdateModel = false;
               templateDefaultContentModelDB = JSON.parse(templateDefaultContentModelDB);

               Object.entries(templateDefaultContentModelDB).map((tData) => {
                  const updatedTemplateDefault = this.contenteditableDirective.templateContextFormat(tData[1], "db");
                  if (tData[0].indexOf("template.") !== -1) {
                     this.mustUpdateModel = true;
                  }
                  const key = tData[0].replace("template.", "");
                  templateDefaultContent[key] = updatedTemplateDefault.value;

                  if (key === 'voiceMessage' && (updatedTemplateDefault.value === undefined || updatedTemplateDefault.value === ''))
                  {
                      templateDefaultContent[key] = 'Insert Text Here';
                  }
                  this.isModelUpdated = updatedTemplateDefault.isModelUpdated;
               });

               this.isModelUpdated = this.mustUpdateModel ? true : this.isModelUpdated;
            }

            previewClone.templateDefaultContent =
               this.fileModel !== undefined ? { smsMessage: "", emailSubject: "" } : templateDefaultContent;

            if (previewClone.model !== undefined) {
               Object.entries(previewClone.model).map((tData) => {
                  const updatedFileModel = this.contenteditableDirective.templateContextFormat(tData[1], "file");
                  model[tData[0]] = updatedFileModel.value;
               });
            }

            previewClone.model = model;
            const templateUpdateModel = {
               updatedModel: previewClone.templateDefaultContent,
               isModelUpdated: this.isModelUpdated,
               updateType: "model",
               type: "",
            };
            this.store.dispatch(
               PreviewEditTemplateActions.templateUpdateModel({
                  templateUpdateModel,
               })
            );

            if (this.isOnlyPreview && Object.keys(previewClone.templateDefaultContent).length !== 0) {
               this.hasValueFromDb = true;
            }

            this.getTemplateState();
            return previewClone;
         })
      );

      if (this.selectedTemplate !== undefined && this.selectedTemplate.templateFileRef !== undefined) {
         this.emailSource$ = this.previewEditTemplateService.loadTemplateSource(this.selectedTemplate.templateFileRef);
      } else {
         if (this.updatedTemplateFileRef !== undefined) {
            this.emailSource$ = this.previewEditTemplateService.loadTemplateSource(this.updatedTemplateFileRef);
         }
      }

      this.subscription.add(
         this.templateLastModified$
            .pipe(filter((lastModified) => lastModified !== undefined))
            .subscribe((lastModified) => {
               this.lastModified = this.pipe.transform(lastModified, "yyyy-MM-dd HH:mm:ss");
               this.isUpdated = false;
            })
      );

      this.viewModel$ = combineLatest([
         this.loading$,
         this.isModelProcessed$,
         this.templateUpdateModel$,
         this.mediaDevices$,
         this.isMediaProcessed$,
         this.isAdmin$,
         this.errorMessage$,
      ]).pipe(
         map(
            ([
               loading,
               isModelProcessed,
               templateUpdateModel,
               mediaDevices,
               isMediaProcessed,
               isAdmin,
               errorMessage,
            ]) => ({
               loading,
               isModelProcessed,
               templateUpdateModel,
               mediaDevices,
               isMediaProcessed,
               isAdmin,
               errorMessage,
            })
         )
      );
   }

   public onCancel(isModelUpdated, errorMessage): void {
      if (
         ((errorMessage === undefined && this.isUpdated) || isModelUpdated || this.isSourceChanged) &&
         !this.isOnlyPreview
      ) {
         let message = "You will loose all unsaved changes. Are you sure you want to exit?";
         if (this.mustUpdateModel) {
            message =
               "You need to save the model to be able to use the template. The template contains data not supported. Are you sure you want to exit?";
         }
         const modalRef = this.modalService.show(
            BsModalConfirmComponent,
            createConfirmModalOptions("Confirm", message, "Yes", "No")
         );

         modalRef.content.onClose.subscribe((dialogResult) => {
            if (dialogResult.isConfirmed) {
               const templateUpdateModel = {
                  updatedModel: {},
                  isModelUpdated: false,
                  updateType: "",
                  type: "",
               };
               this.store.dispatch(
                  PreviewEditTemplateActions.templateUpdateModel({
                     templateUpdateModel,
                  })
               );
               if (this.isSourceChanged) {
                  this.store.dispatch(
                     PreviewEditTemplateActions.deleteTmpFile({
                        fileName: this.updatedTemplateFileRef,
                     })
                  );
               }
               this.previewBsModalRef.hide();
               this.onClose.next(dialogResult);
            }
         });
      } else {
         this.previewBsModalRef.hide();
      }
   }

   sourceChanged(event): void {
      this.isSourceChanged = true;
      this.updatedSource = event;
      this.isUpdated = true;
   }

   isMediaChanged(event): void {
      this.isMediaUpdated = true;
   }

   getIsPnovaVoiceMediaType() {
       let isPnovaVoiceMediaType = false;
       this.subscription.add(
           this.mediaDevices$.pipe().subscribe((mediaDevices) =>
               mediaDevices.map(media => {
                       const mediaDevice: any = {...media};
                       if (mediaDevice.type === 'voice' && mediaDevice.device.indexOf('pnova') !== -1) {
                           isPnovaVoiceMediaType = true;
                       }
                   }
               )
           ));

       return isPnovaVoiceMediaType;
   }

   templateFileRefChange(event): void {
      this.updatedTemplateFileRef = event;
   }

   saveTemplate(selectedUserId, updatedModel, isModelUpdated, mediaDevices, shouldOnlyUpdateFileRef): void {
      let message = "";

      // We will check if its default template that needs to be updated.
      // template selected from left side (i.e ALL) can only be considered as Defaults
      if (
         this.canEditDefault &&
         !this.selectedTemplate.templateName.toLowerCase().match(/^\d/) &&
         this.selectedTemplate.templateFileRef.toLowerCase().indexOf("default") === 0
      ) {
         message =
            "You are making changes in Default template, which will affect all Customers using it. Are you sure you want to save?";
      } else if (this.isSourceChanged && isModelUpdated && this.isMediaUpdated) {
         message = "There are changes in Source, Media and Model of the template. Are you sure you want to save?";
      } else if (this.isSourceChanged && isModelUpdated) {
         message = "There are changes in both Source and Model of the template. Are you sure you want to save?";
      } else if (this.isSourceChanged && this.isMediaUpdated) {
         message = "There are changes in both Source and Media of the template. Are you sure you want to save?";
      } else if (isModelUpdated && this.isMediaUpdated) {
         message = "There are changes in both Model and Media of the template. Are you sure you want to save?";
      } else if (this.isSourceChanged) {
         message = "There are changes in Source of the template. Are you sure you want to save?";
      } else if (isModelUpdated) {
         message = "The template is already assigned to the current user. Are you sure you want to save?";
      } else if (this.isMediaUpdated) {
         message = "There are changes in Media of the template. Are you sure you want to save?";
      }

      this.save(message, selectedUserId, updatedModel, isModelUpdated, mediaDevices, shouldOnlyUpdateFileRef);
   }

   saveAsCustomTemplate(selectedUserId, updatedModel, mediaDevices): void {
      console.log("saveAsCustomTemplate");
      // template: any, userId: number, model: any, source: any
      const templateDefaultContent = this.cleanUpUpdateModel(updatedModel);

      const saveType = this.isSourceChanged ? "source" : "both";
      this.startSaving();

      if (this.totalStrategies <= 0) {
         this.store.dispatch(
            PreviewEditTemplateActions.saveAsCustomTemplate({
               template: this.selectedTemplate,
               userId: selectedUserId,
               model: templateDefaultContent,
               source: this.updatedSource,
               saveType,
            })
         );
      } else {
         // DIALOG TO SHOW TO UPDATE STRATEGIES
         const modalRef3 = this.modalService.show(
            BsModalConfirmComponent,
            createConfirmModalOptions(
               "Confirm",
               `Template is mapped with ${this.totalStrategies} strategy(s) of selected user.
                    Do you want to save the model and update the strategy(s) with new template?`,
               "Yes",
               "No"
            )
         );

         modalRef3.content.onClose.subscribe((dialogResult) => {
            if (dialogResult.isConfirmed) {
               this.store.dispatch(
                  PreviewEditTemplateActions.saveAsCustomTemplate({
                     template: this.selectedTemplate,
                     userId: selectedUserId,
                     model: templateDefaultContent,
                     source: this.updatedSource,
                     saveType,
                  })
               );
            } else {
               this.stopSaving();
               console.log(
                  "Do Nothing, as user need to create custom template and update strategy but selected not to."
               );
            }
         });
      }

      this.subscription.add(
         this.savedCustomTemplate$.subscribe((isSavedCustom) => {
            if (isSavedCustom) {
               if (this.isSourceChanged) {
                  this.store.dispatch(
                     PreviewEditTemplateActions.deleteTmpFile({
                        fileName: this.updatedTemplateFileRef,
                     })
                  );
               }
               this.previewBsModalRef.hide();
            }
         })
      );
   }

   shouldSaveAsCustom(selectedUserId): boolean {
      return (
         !this.canEditDefault &&
         (this.isMappedToOtherUser || this.isFileUsedByOtherUser) &&
         selectedUserId !== undefined &&
         selectedUserId !== 0
      );
   }

   saveChanges(selectedUserId, updatedModel, isModelUpdated, mediaDevices): any {
      let templateFolder = "";
      let templateName = "";
      let shouldOnlyUpdateFileRef = false; // when the model is changed but the file is still referring to default
      const updateModelCleaned = this.cleanUpUpdateModel(updatedModel);
      this.startSaving();

      try {
         templateFolder = this.selectedTemplate.templateFileRef.split(/\//)[0];
         templateName = this.selectedTemplate.templateName.split("-")[0];
         shouldOnlyUpdateFileRef = templateFolder === "default" && +templateName === +selectedUserId;
      } catch (e) {
         console.log();
      }

      if (+templateFolder === +selectedUserId || this.canEditDefault || shouldOnlyUpdateFileRef) {
         this.saveTemplate(selectedUserId, updateModelCleaned, isModelUpdated, mediaDevices, shouldOnlyUpdateFileRef);
      } else {
         const modalRef2 = this.modalService.show(
            BsModalConfirmComponent,
            createConfirmModalOptions(
               "Confirm",
               "Template is not stored in folder of selected user. " +
                  "Do you want to save the template as a custom one in the selected users' folder?",
               "Yes",
               "No"
            )
         );

         modalRef2.content.onClose.subscribe((dialogResult) => {
            if (dialogResult.isConfirmed) {
               this.saveAsCustomTemplate(selectedUserId, updatedModel, mediaDevices);
            } else {
               this.stopSaving();
               console.log("Do Nothing, as user need to create custom template but selected not to.");
            }
         });
      }
   }

   save(message, selectedUserId, updatedModel, isModelUpdated, mediaDevices, shouldOnlyUpdateFileRef): void {
      const modalRef2 = this.modalService.show(
         BsModalConfirmComponent,
         createConfirmModalOptions("Editing of template", message, "Yes", "No")
      );

      modalRef2.content.onClose.subscribe((dialogResult) => {
         if (dialogResult.isConfirmed) {
            this.store.dispatch(
               PreviewEditTemplateActions.isTemplateUsed({
                  templateId: this.selectedTemplateId,
                  userId: selectedUserId,
                  communicationsCategoryId: this.categoryType,
                  updatedModel: updatedModel,
                  shouldOnlyUpdateFileRef,
                  mediaDevices,
                  isModelUpdated,
               })
            );
         } else {
            this.stopSaving();
            console.log(
               "Changes are not reverted back to original. It will be reverted when we close the preview window."
            );
         }
      });
   }

   getTemplateState(): void {
      this.subscription.add(
         this.templateState$
            .pipe(filter((templateState) => templateState !== undefined))
            .subscribe((templateState: any) => {
               if (templateState.isTemplateUsed !== undefined) {
                  if (templateState.isTemplateUsed) {
                     if (this.canUpdateTemplateInUsePermission) {
                        const modalRef3 = this.modalService.show(
                           BsModalConfirmComponent,
                           createConfirmModalOptions(
                              "Editing of template",
                              "The template is being used in '" + templateState.names + "'. Do you want to continue?",
                              "Yes",
                              "No"
                           )
                        );
                        modalRef3.content.onClose.subscribe((dialogResult) => {
                           if (dialogResult.isConfirmed) {
                              this.saveAndUpdate(
                                 templateState.userId,
                                 templateState.shouldOnlyUpdateFileRef,
                                 templateState.updatedModel,
                                 templateState.mediaDevices,
                                 templateState.isModelUpdated
                              );
                           }
                        });
                     } else {
                        this.modalService.show(
                           BsModalAlertComponent,
                           createAlertModalOptions(
                              "Alert",
                              "The template is being used in '" +
                                 templateState.names +
                                 "'. Please stop the job or change the strategy before making changes in template."
                           )
                        );

                        this.stopSaving();
                     }
                  } else {
                     this.saveAndUpdate(
                        templateState.userId,
                        templateState.shouldOnlyUpdateFileRef,
                        templateState.updatedModel,
                        templateState.mediaDevices,
                        templateState.isModelUpdated
                     );
                  }
               }
            })
      );
   }

   saveAndUpdate(selectedUserId, shouldOnlyUpdateFileRef, updatedModel, mediaDevices, isModelUpdated): void {
      if (this.isSourceChanged) {
         let fileName = this.isFromUploadDialog ? this.templateFilename : this.selectedTemplate.templateFileRef;
         const fileFolder = shouldOnlyUpdateFileRef ? selectedUserId.toString() : "";

         //if the file name should be updated remove the folder (default)
         fileName = shouldOnlyUpdateFileRef ? fileName.split(/\//)[1] : fileName;

         const template = {
            templatePath: fileName,
            fileSource: this.updatedSource,
            createIfNotExists: true,
            fileFolder,
            shouldOnlyUpdateFileRef,
         };
         this.store.dispatch(
            PreviewEditTemplateActions.saveSource({
               template,
               templateId: this.selectedTemplateId,
               userId: selectedUserId,
               model: updatedModel,
               templateFilename: this.templateFileRef,
               isConfirm: this.selectedTemplate.isConfirm,
            })
         );
         this.isSourceChanged = false;
         this.store.dispatch(
            PreviewEditTemplateActions.deleteTmpFile({
               fileName: this.updatedTemplateFileRef,
            })
         );
         this.updatedTemplateFileRef = this.templateFileRef;
      }

      if (this.isMediaUpdated) {
         this.store.dispatch(
            PreviewEditTemplateActions.saveMedia({
               mediaDevices,
               templateId: this.selectedTemplateId,
            })
         );
         this.isMediaUpdated = false;
      }

      if (isModelUpdated) {
         const templateDefaultContent = {};
         Object.entries(updatedModel).map((tData) => {
            templateDefaultContent[tData[0]] = this.contenteditableService.validateTemplateBeforeSave(tData);
         });

         const templateUpdateModel = {
            updatedModel: templateDefaultContent,
            isModelUpdated: false,
            updateType: "autoCorrectAndSaving",
            type: "",
         };
         this.store.dispatch(
            PreviewEditTemplateActions.templateUpdateModel({
               templateUpdateModel,
            })
         );

         this.saveModel(templateDefaultContent, selectedUserId);
      }

      if (!isModelUpdated && !this.isMediaUpdated && !this.isSourceChanged) {
         this.stopSaving();
      }
   }

   saveModel(updatedModel, selectedUserId): void {
      const template: any = { ...this.selectedTemplate };
      template.templateDefaultContent = JSON.stringify(updatedModel);

      this.store.dispatch(
         PreviewEditTemplateActions.saveModel({
            templateId: this.selectedTemplateId,
            isConfirmed: this.canEditDefault,
            forRevert: this.selectedTemplate,
            template,
            userId: selectedUserId,
            lastModified: this.lastModified,
         })
      );
   }

   ngOnDestroy(): void {
      this.stopSaving();
      this.subscription.unsubscribe();
      sessionStorage.removeItem("selectedMediaType");
   }

   selectedTab(index): void {
      this.activeIndex = index;
      this.smsConversionService.displayButton(false);
   }

   openAdaptiveRulesDialog(selectedUserId): void {
      const modalRef2 = this.modalService.show(AdaptiveRulesComponent, {
         initialState: {
            okLabel: "Save",
            cancelLabel: "Close",
            userId: selectedUserId,
            categoryType: this.categoryType,
         },
         backdrop: "static",
         class: "modal-lg",
         keyboard: false, // disable esc key
      });

      modalRef2.content.onClose.subscribe((_) => {
         this.store.dispatch(
            PreviewEditTemplateActions.getTemplateParameters({
               userId: selectedUserId,
               categoryType: this.categoryType,
            })
         );
      });
   }

   triggerConversionSMS(): void {
      this.smsConversionService.triggerConversion();
   }

   private startSaving() {
      this.store.dispatch(PreviewEditTemplateActions.setSavingStatus({ savingStatus: true }));
   }

   private stopSaving() {
      this.store.dispatch(PreviewEditTemplateActions.setSavingStatus({ savingStatus: false }));
   }

   /**
    * This is helping to remove no breaking space character (&nbsp)
    * also remove content editable directive
    * from every template variable in updateModel
    * fix-#18206336
    * @param updateModel: Record<string, string>
    */
   private cleanUpUpdateModel(updateModel: Record<string, string>): Record<string, string> {
      return Object.keys(updateModel).reduce((updateModelClean, templateVariableKey) => {
         let variableContent: any = updateModel[templateVariableKey].toString();
         const replaceIf =
            templateVariableKey.includes("voice") ||
            templateVariableKey.includes("sms") ||
            templateVariableKey.includes("emailSubject");

         let templateVariable: any;
         if (variableContent) {
            variableContent = variableContent.replaceAll('contenteditable="false">', ">");
            variableContent = replaceIf ? variableContent.replace(/&nbsp;/g, " ") : variableContent;
            if (templateVariableKey === 'voiceMessage' && variableContent === 'Insert Text Here')
            {
                variableContent= '';
            }

            templateVariable = {
               ...updateModelClean,
               [templateVariableKey]: variableContent,
            };
         } else {
            templateVariable = { ...updateModelClean, [templateVariableKey]: "" };
         }
         return templateVariable;
      }, {});
   }
}
